import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSearchResults } from "./useSearchResults";
import type {
  ISearchPaginationBlockDto,
  ISearchResultDto,
  ISearchTabDto,
  ILabelQueryDto,
} from "../../../contract/generated/bff/src/models";

export interface UseSearchResultsPageResult {
  tabs: ISearchTabDto[];
  results: ISearchResultDto[];
  pagination: ISearchPaginationBlockDto | undefined;
  relatedSearches: ILabelQueryDto[];
  spellingSuggestion: ILabelQueryDto | null;
  loading: boolean;
  error: Error | null;
  selectedTab: ISearchTabDto | null;
  showSkeleton: boolean;
  handleSelect: (tab: ISearchTabDto | null) => void;
  setCurrentPage: (page: number) => void;
}

export const useSearchResultsPage = (): UseSearchResultsPageResult => {
  const [searchParams] = useSearchParams();
  const term = searchParams.get("term");

  const [selectedTab, setSelectedTab] = useState<ISearchTabDto | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  // Tracks whether we've done the initial sync from BFF selected:true tab
  const [initialized, setInitialized] = useState(false);

  const { data, relatedSearches, spellingSuggestion, loading, error } =
    useSearchResults(term, selectedTab?.filter, currentPage);

  const tabs = useMemo(() => data?.tabs ?? [], [data]);
  const results = useMemo(() => data?.results ?? [], [data]);
  const pagination = data?.pagination;

  // On first BFF response, select whichever tab has selected:true
  useEffect(() => {
    if (tabs.length > 0 && !initialized) {
      setSelectedTab(tabs.find((tab) => tab.selected) ?? null);
      setInitialized(true);
    }
  }, [tabs, initialized]);

  // Reset page to 1 when term or selected tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [term, selectedTab]);

  // BFF tabs arrived but not yet initialized — suppress the unfiltered flash
  const isFilterPending = tabs.length > 0 && !initialized;
  const showSkeleton = loading || isFilterPending;

  const handleSelect = (tab: ISearchTabDto | null) => {
    setSelectedTab(tab);
    setCurrentPage(1);
  };

  return {
    tabs,
    results,
    pagination,
    relatedSearches,
    spellingSuggestion,
    loading,
    error,
    selectedTab,
    showSkeleton,
    handleSelect,
    setCurrentPage,
  };
};
